'use client';

import type { LiveVoiceSession } from '@/lib/live-voice-session';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { VoiceCallStatus } from '@/components/org-chat/voice-call-bar-view';
import { isPushToTalkKeyboardTargetExempt } from '@/lib/realtime-voice-keyboard';

type Args = {
  open: boolean;
  sessionId: string | null;
  sessionRef: React.MutableRefObject<LiveVoiceSession | null>;
  levelsRef?: React.MutableRefObject<{ user: number; agent: number }>;
  initialInputMode?: 'vad' | 'ptt';
  enableGlobalPttKeyboard?: boolean;
};

export function useVoiceCallUiController({
  open,
  sessionId,
  sessionRef,
  levelsRef,
  initialInputMode = 'vad',
  enableGlobalPttKeyboard = true,
}: Args) {
  const [status, setStatus] = useState<VoiceCallStatus>(
    open ? 'connecting' : 'idle'
  );
  const [callElapsedMs, setCallElapsedMs] = useState(0);
  const [inputMode, setInputMode] = useState<'vad' | 'ptt'>(initialInputMode);
  const [micMuted, setMicMuted] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [pttActive, setPttActive] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const callConnectedAtRef = useRef<number | null>(null);
  const statusRef = useRef<VoiceCallStatus>('idle');
  const inputModeRef = useRef<'vad' | 'ptt'>(initialInputMode);
  const micMutedRef = useRef(false);
  const pttActiveRef = useRef(false);
  const micUnmutedAtRef = useRef<number | null>(null);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    if (!open || callConnectedAtRef.current === null) return;
    const updateElapsed = () => {
      const connectedAt = callConnectedAtRef.current;
      setCallElapsedMs(connectedAt === null ? 0 : Date.now() - connectedAt);
    };
    updateElapsed();
    const interval = window.setInterval(updateElapsed, 1_000);
    return () => window.clearInterval(interval);
  }, [open, sessionId, status]);

  useEffect(() => {
    inputModeRef.current = inputMode;
  }, [inputMode]);

  useEffect(() => {
    micMutedRef.current = micMuted;
  }, [micMuted]);

  useEffect(() => {
    pttActiveRef.current = pttActive;
  }, [pttActive]);

  const applyMuteState = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    const muted =
      micMutedRef.current ||
      (inputModeRef.current === 'ptt' && !pttActiveRef.current);
    session.mute(muted);
    micUnmutedAtRef.current = muted ? null : Date.now();
  }, [sessionRef]);

  useEffect(() => {
    applyMuteState();
  }, [applyMuteState, inputMode, micMuted, pttActive]);

  useEffect(() => {
    if (!levelsRef) return;
    levelsRef.current = {
      user: isCapturing && !micMuted ? 0.75 : 0,
      agent: status === 'speaking' ? 0.65 : 0,
    };
  }, [isCapturing, levelsRef, micMuted, status]);

  useEffect(() => {
    if (!open || inputMode !== 'ptt' || !enableGlobalPttKeyboard) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || event.repeat) return;
      if (isPushToTalkKeyboardTargetExempt(event.target)) return;
      event.preventDefault();
      setPttActive(true);
      setIsCapturing(true);
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code !== 'Space') return;
      event.preventDefault();
      setPttActive(false);
      setIsCapturing(false);
    };
    const releasePushToTalk = () => {
      setPttActive(false);
      setIsCapturing(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', releasePushToTalk);
    document.addEventListener('visibilitychange', releasePushToTalk);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', releasePushToTalk);
      document.removeEventListener('visibilitychange', releasePushToTalk);
    };
  }, [enableGlobalPttKeyboard, inputMode, open]);

  return {
    status,
    setStatus,
    callElapsedMs,
    setCallElapsedMs,
    inputMode,
    setInputMode,
    micMuted,
    setMicMuted,
    isCapturing,
    setIsCapturing,
    pttActive,
    setPttActive,
    errorText,
    setErrorText,
    callConnectedAtRef,
    statusRef,
    inputModeRef,
    micMutedRef,
    pttActiveRef,
    micUnmutedAtRef,
    applyMuteState,
  };
}
